import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { MdErrorOutline } from 'react-icons/md';
import { getAccessToken } from '@podus/auth';
import AuthLayout from '@/components/AuthLayout';
import { API_BASE } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useNoIndex } from '@/lib/useNoIndex';

const SYSTEM_KEY = import.meta.env.VITE_PODUS_SYSTEM_KEY ?? 'G@V3l2023-new-podus-v001233-SyS';

interface ProfileResponse {
    response?: {
        data?: {
            id: number;
            firstName?: string | null;
            lastName?: string | null;
            email: string;
            accountType: string;
        };
    };
}

/**
 * Landing point for the auth handoff.
 *
 * auth.podus.org returns with `?accessToken=`; before trusting it we ask the
 * Podus API who it belongs to and refuse anything that isn't an Admin or Super
 * Admin. A staff-issued token is a Podus token, not an ambassador one, so the
 * role is decided here rather than by the token itself.
 */
export default function AdminCallback() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const [error, setError] = useState('');
    const startedRef = useRef(false);
    useNoIndex();

    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;

        // Prefer the token in the URL; fall back to the shared cookie for the
        // case where the user already had a live Podus session.
        const token = params.get('accessToken') ?? getAccessToken();
        if (!token) {
            setError('No sign-in token was returned. Please try again.');
            return;
        }

        fetch(`${API_BASE}/users/profile`, {
            headers: { Authorization: `Bearer ${SYSTEM_KEY}:${token}` },
        })
            .then(async (res) => {
                if (!res.ok) throw new Error('rejected');
                return (await res.json()) as ProfileResponse;
            })
            .then((body) => {
                const profile = body?.response?.data;
                if (!profile) throw new Error('rejected');

                if (profile.accountType !== 'Admin' && profile.accountType !== 'Super Admin') {
                    setError('That account does not have access to the ambassador programme.');
                    return;
                }

                const name = `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim();
                login(token, {
                    id: profile.id,
                    name: name || profile.email,
                    email: profile.email,
                    role: 'admin',
                });
                navigate('/admin', { replace: true });
            })
            .catch(() => setError('We could not verify that sign-in. Please try again.'));
    }, [params, login, navigate]);

    if (error) {
        return (
            <AuthLayout
                title="Sign in failed"
                subtitle={error}
                footer={
                    <Link to="/admin/sign-in" className="font-bold text-primary">
                        Try again
                    </Link>
                }
            >
                <div className="rounded-xl border border-[#E0E0E0] bg-[#fafafa] p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <MdErrorOutline size={22} />
                    </span>
                    <p className="mt-4 text-[14px] leading-6 text-[#747474]">
                        Only Podus Admin and Super Admin accounts can open the programme dashboard. If you believe you
                        should have access, contact your Podus administrator.
                    </p>
                </div>
            </AuthLayout>
        );
    }

    return (
        <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-white px-6 text-center">
            <img src="/logo.svg" alt="Podus" className="h-9 w-auto" />
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-200 border-t-primary" />
            <p className="text-sm text-[#747474]">Verifying your admin access…</p>
        </div>
    );
}
